import React, { Component, Fragment } from 'react';
// components
import { Row, Col } from 'reactstrap';
import { Radio, Checkbox, Date as DateInput, Label } from 'components/inputs';
import { LEGACY_URL } from 'components/constants';
// functions
import julian from 'julian';
import moment from 'moment';
import { toJulian } from 'functions/dates';
import { onCheckboxChange, onInputChange, onNumberChange } from 'functions/events';

// a reset the base never answered. 0 ( always ) is an answer, so it is not blank.
const isBlankReset = value => value === undefined || value === null || value === '';

// a reset left over from a cycle that already ended: last year every base was defaulted to
// last year's beginning of the school year, which is not a choice for this year. Always ( 0 )
// means all years on purpose, so it is never stale.
const isStaleReset = ( value, options ) => {
  if ( isBlankReset( value ) || !options.length ) return false;
  const jd = parseInt( value, 10 );
  return jd > 0 && jd < parseInt( options[0].jd, 10 );
}

export class SettingsRow extends Component {

  state = {
    disabled: false,
    storeResetOptions: [],
    checked: false, // for radio buttons under store miles settings
  };

  // once the commander picks a store reset, do not roll it forward to the default
  userPickedReset = false;
  // a date-picker click can land on the radio underneath when the calendar closes
  ignoreResetRadiosUntil = 0;

  componentDidMount() {
    this.applyDefaultPhotoUpdateIfNeeded();
    fetch( `${LEGACY_URL}/api/registration/store_resets.php` )
      .then( res => res.json() )
      .then( resets => {
        const options = resets.data || [];
        this.setState({ storeResetOptions: options }, () => {
          this.applyDefaultResetsIfNeeded( options );
        });
      })
      // the base still gets the default reset on the server, so just leave the presets empty
      .catch( () => {} )
  }

  componentDidUpdate( prevProps ) {
    const prev = prevProps.base || {};
    const curr = this.props.base || {};
    if ( prev.allow_parent_photo_update !== curr.allow_parent_photo_update ) {
      this.applyDefaultPhotoUpdateIfNeeded();
    }
    if (
      prev.store_reset !== curr.store_reset ||
      prev.one_time_prize_reset !== curr.one_time_prize_reset
    ) {
      this.applyDefaultResetsIfNeeded( this.state.storeResetOptions );
    }
  }

  applyDefaultPhotoUpdateIfNeeded = () => {
    const { allow_parent_photo_update } = this.props.base || {};
    if (
      allow_parent_photo_update === undefined ||
      allow_parent_photo_update === null ||
      allow_parent_photo_update === ''
    ) {
      this.props.onUpdate({ allow_parent_photo_update: 1 });
    }
  }

  /**
   * A reset the base has not answered for this year. While registering, a date left
   * over from a previous year counts as unanswered and rolls forward to the default.
   */
  needsDefaultReset = ( value, options ) => {
    if ( this.userPickedReset ) return false;
    return isBlankReset( value )
      || ( this.props.registering && isStaleReset( value, options ) );
  }

  /**
   * Persist the visually defaulted preset (index 1 — beginning of school year)
   * when the school has no store_reset / one_time_prize_reset for this year.
   */
  applyDefaultResetsIfNeeded = ( options = this.state.storeResetOptions ) => {
    if ( this.userPickedReset ) return;
    if ( !options || options.length < 2 ) return;
    const defaultJd = parseInt( options[1].jd, 10 );
    if ( !Number.isFinite( defaultJd ) ) return;

    const { store_reset, one_time_prize_reset } = this.props.base || {};
    const updates = {};
    if ( this.needsDefaultReset( store_reset, options ) ) {
      updates.store_reset = defaultJd;
    }
    if ( this.needsDefaultReset( one_time_prize_reset, options ) ) {
      updates.one_time_prize_reset = defaultJd;
    }
    if ( Object.keys( updates ).length ) {
      this.props.onUpdate( updates );
    }
  }

  onChange = onInputChange( this.props.onUpdate );
  handleCheckbox = onCheckboxChange( this.props.onUpdate );
  onNumberChange = onNumberChange( this.props.onUpdate );

  ignoreResetRadioClick = () =>
    Date.now() < this.ignoreResetRadiosUntil;

  onDateChage = date => {
    this.userPickedReset = true;
    // the calendar unmounts on this same click, which can hit a preset radio underneath
    this.ignoreResetRadiosUntil = Date.now() + 400;
    this.props.onUpdate({ store_reset: date ? toJulian( date ) : date });
  };

  changeSchoolReset = event => {
    if ( this.ignoreResetRadioClick() ) return;
    this.userPickedReset = true;
    const store_reset = event.target.value;
    this.props.onUpdate({ store_reset });
    this.setState({ disabled: true, checked: true });
  }

  enableCustom = event => {
    if ( this.ignoreResetRadioClick() ) return;
    this.userPickedReset = true;
    const store_reset = event.target.value;
    this.props.onUpdate({ store_reset });
    this.setState({ disabled: false, checked: false });
  }

  render () {
    const { base } = this.props;
    let {
      pic_mission_type,   store_reset,  school_gender,
      print_parent_tasks, allow_parent_tasks, allow_parent_photo_update, rewards,
      one_time_prize_reset
    } = base;

    // only check the default preset while the base has not answered for this year
    const options = this.state.storeResetOptions || [];
    const store_reset_blank = this.needsDefaultReset( store_reset, options );
    const prize_reset_blank = this.needsDefaultReset( one_time_prize_reset, options );
    const store_reset_jd = parseInt(store_reset, 10);
    store_reset = store_reset > 0 ? moment( julian.toDate( store_reset ) ) : undefined;

    // props for all inputs
    const checkboxProps = { onChange: this.handleCheckbox };
    const schoolGenderProps = { name: 'school_gender', onChange: this.onChange }
    const missionTypeProps = { name: 'pic_mission_type', onChange: this.onNumberChange }
    const storeResetProps = { name: 'store_miles_reset', onChange: this.onChange }
    const storeResetOptions = options;
    const isPreset = storeResetOptions.some(r => parseInt(r.jd, 10) === store_reset_jd);
    // a date from a previous year is being rolled forward, not kept as a custom date
    const isCustom = Number.isInteger(store_reset_jd) && store_reset_jd > 0
      && !isPreset && !store_reset_blank;

    return (
      <Row id='SettingsRow'>
        <Col xs={12} sm={6} xl={4}>
          <Label>Base Gender</Label>
          <Radio value='M' 
              { ...schoolGenderProps }
              checked={ school_gender === 'M' } >
            Boys
          </Radio>

          <Radio value='F'
              { ...schoolGenderProps }
              checked={ school_gender === 'F' }>
            Girls
          </Radio>

          <Radio value='B'
              { ...schoolGenderProps }
              checked={ school_gender === 'B' }>
            Both
          </Radio>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <Label>Mission Sheet Type</Label>
          <Radio value='1' 
              { ...missionTypeProps }
              checked={ pic_mission_type === 1 } >
            No Pictures
          </Radio>

          <Radio value='2'
              { ...missionTypeProps }
              checked={ pic_mission_type === 2 }>
            Small Pictures
          </Radio>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <Label>Custom Parent Tasks</Label>
          <Checkbox checked={!!allow_parent_tasks} name='allow_parent_tasks' { ...checkboxProps} >
            Allow
          </Checkbox>
          <Checkbox checked={!!print_parent_tasks} name='print_parent_tasks' { ...checkboxProps} >
            Include on printable mission sheets
          </Checkbox>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <Label>Parent Photo Updates</Label>
          <Checkbox
              checked={ allow_parent_photo_update !== 0 && allow_parent_photo_update !== false && allow_parent_photo_update !== '0' }
              name='allow_parent_photo_update'
              { ...checkboxProps} >
            Allow parents to update child photo
          </Checkbox>
        </Col>

        {/*<Col xs={12} sm={6} xl={4}>*/}
        {/*  <Label>Amount of Boys Chidon Posters</Label>*/}
        {/*  <Input type='number'*/}
        {/*      name='chidon_posters_boys'*/}
        {/*      onChange={ this.onNumberChange }*/}
        {/*      value={ chidon_posters_boys || 0 } />*/}
        {/*</Col>*/}
        {/*<Col xs={12} sm={6} xl={4}>*/}
        {/*  <Label>Amount of Girls Chidon Posters</Label>*/}
        {/*  <Input type='number'*/}
        {/*      name='chidon_posters_girls'*/}
        {/*      onChange={ this.onNumberChange }*/}
        {/*      value={ chidon_posters_girls || 0 } />*/}
        {/*</Col>*/}

      { rewards > 0 &&
      <Fragment>
        <Col sm={12} className='special-options'>
          <p className='title'>Store Miles Settings</p>
          <Label>Allow students to spend miles earned from:</Label>

          { storeResetOptions && storeResetOptions.map((reset, index) => (
            <Fragment key={ reset.jd }>
              <Radio value={ reset.jd }
                name='store_miles_reset'
                onChange={ this.changeSchoolReset }
                checked={ store_reset_jd === parseInt(reset.jd, 10) || (index === 1 && store_reset_blank) }>
                { reset.title } ({ reset.hDate } / { reset.date })
              </Radio>
              <br />
            </Fragment>
          ))}

          <Radio key={0} id='store_reset' value='0'
            name='store_miles_reset'
            onChange={ this.changeSchoolReset }
            checked={ store_reset_jd === 0 }>
            Always (This includes all miles from previous years)
          </Radio>
          <br />

          <Radio key={ toJulian( moment() ) } value={ toJulian( moment() ) }
            name='store_miles_reset'
            { ...storeResetProps }
            checked={ isCustom }
            onChange={ this.enableCustom }>
            Custom Date:
          </Radio>
          <br />

          <DateInput value={ store_reset }
            disabled={ !isCustom }
            withPortal
            onChange={ this.onDateChage } />
        </Col>

        <Col sm={12} className='special-options'>
          <p className='title'>One Time Prize Settings</p>
          <Label>Students should not be able to purchase more than one of the "one time prize" if purchased from:</Label>
          { this.state.storeResetOptions && this.state.storeResetOptions.map((reset, i) => (
            <Fragment key={ reset.jd }>
              <Radio value={ reset.jd }
                name='one_time_prize_reset'
                onChange={ this.onChange }
                checked={ parseInt(one_time_prize_reset, 10) === parseInt(reset.jd, 10) || (i === 1 && prize_reset_blank) }>
                { reset.title } ({ reset.hDate } / { reset.date })
              </Radio>
              <br />
            </Fragment>
          ))}
        </Col>
      </Fragment>
      }
      </Row>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/base-managment/base/includes/rows/SettingsRow.jsx